/**
 @license
 Copyright (c) 2015 The Polymer Project Authors. All rights reserved.
 This code may only be used under the BSD style license found at http://polymer.github.io/LICENSE.txt
 The complete set of authors may be found at http://polymer.github.io/AUTHORS.txt
 The complete set of contributors may be found at http://polymer.github.io/CONTRIBUTORS.txt
 Code distributed by Google as part of the polymer project is also
 subject to an additional IP rights grant found at http://polymer.github.io/PATENTS.txt
 */
/**
 A Polymer address form element that offers suggestions from the Google Maps API as you type.

 Example:

 <cbar-address-autocomplete></cbar-address-autocomplete>

 @demo
 */
/*
  FIXME(polymer-modulizer): the above comments were extracted
  from HTML and may be out of place here. Review them and
  then delete this comment!
*/
import '@polymer/polymer/polymer-legacy.js';

import '@cbar/google-apis/google-maps-api.js';
import '@polymer/paper-input/paper-input.js';
import {Polymer} from '@polymer/polymer/lib/legacy/polymer-fn.js';
import {html} from '@polymer/polymer/lib/utils/html-tag.js';

Polymer({
  _template: html`
    <style>
      :host {
        display: block;
        box-sizing: border-box;
      }

      paper-input {
        @apply(--cbar-address-autocomplete-paper-input);
      }
    </style>

    <template is="dom-if" if="[[!useExternalApi]]">
        <google-maps-api api-key="[[apiKey]]" client-id="[[clientId]]" version="3.exp" libraries="places" on-api-load="_mapsApiLoaded"></google-maps-api>
    </template>

    <slot name="lookup-address" data-input-id="lookupAddressInput">
      <paper-input id="lookupAddressInput" type="text" label="Lookup Address" value="{{lookupAddress}}"></paper-input>
    </slot>
    
    <slot name="title" data-input-id="title">
      <paper-input id="titleInput" type="text" label="Title" value="{{title}}" hidden$="[[noTitle]]"></paper-input>
    </slot>
    
    <slot name="country" data-input-id="countryInput">
      <paper-input id="countryInput" type="text" label="Country" value="{{country}}"></paper-input>
    </slot>
    
    <slot name="postcode" data-input-id="zipCodeInput">
      <paper-input id="zipCodeInput" type="text" label="Postcode" value="{{postcode}}"></paper-input>
    </slot>
    
    <slot name="state" data-input-id="stateInput">
      <paper-input id="stateInput" type="text" label="State" value="{{state}}"></paper-input>
    </slot>
    
    <slot name="suburb" data-input-id="cityInput">
      <paper-input id="cityInput" type="text" label="City" value="{{suburb}}"></paper-input>
    </slot>

    <slot name="street-address" data-input-id="address1Input">
      <paper-input id="address1Input" type="text" label="Street Address 1" value="{{streetAddress}}"></paper-input>
    </slot>

    <slot name="street-address-2" data-input-id="address2Input">
      <paper-input id="address2Input" type="text" label="Street Address 2" value="{{streetAddress2}}"></paper-input>
    </slot>

    <slot name="street-address-3" data-input-id="address3Input">
      <paper-input id="address3Input" type="text" label="Street Address 3" value="{{streetAddress3}}"></paper-input>
    </slot>
`,

  is: 'cbar-address-autocomplete',

  properties: {
    /**
     * A Google Maps API key. To obtain an API key, see developers.google.com/maps/documentation/javascript/tutorial#api_key.
     */
    apiKey: String,

    /**
     * A Google Maps API for Business Client ID. To obtain a Maps API for Business Client ID, see developers.google.com/maps/documentation/business/.
     * If set, a Client ID will take precedence over an API Key.
     */
    clientId: String,

    /**
     * The address to lookup
     */
    lookupAddress: {
      type: String,
      observer: '_initialize',
      notify: true
    },
    title: {
      type: String,
      notify: true
    },
    streetAddress: {
      type: String,
      notify: true
    },
    streetAddress2: {
      type: String,
      notify: true,
    },
    streetAddress3: {
      type: String,
      notify: true,
    },
    state: {
      type: String,
      notify: true
    },
    suburb: {
      type: String,
      notify: true
    },
    postcode: {
      type: String,
      notify: true
    },
    country: {
      type: String,
      notify: true
    },
    useExternalApi: {
      type: Boolean,
      value: false
    },
    optionalFields: {
      type: Array,
      value: [
        'title',
        'address2',
        'address3'
      ]
    },
    noTitle: {
      type: Boolean,
      value: false
    },
    place: {
      type: Object,
      value: false
    }
  },

  ready: function () {
    this._slotChanged = this._slotChanged.bind(this);

    this.root.querySelectorAll('slot').forEach(slot => {
      slot.addEventListener('slotchange', this._slotChanged);

      // Trigger handler once manually for each slot, as for some
      // browsers, the slotchange event does not get called initially.
      this._saveSlottedNodeToIdHash(slot);
    });
  },

  attached: function () {
    this._initialize();
  },

  detached: function () {
    this.autocomplete = null;
  },

  _mapsApiLoaded: function () {
    this._initialize();
  },

  _initialize: function () {
    if (!this.isAttached) {
      return;
    }

    if (!(window.google && window.google.maps)) {
      return;
    }

    const input = this._tryGetHtmlInput(this.$.lookupAddressInput);
    if (!(input instanceof HTMLInputElement)) {
      return;
    }

    if (input.__autocomplete) {
      return;
    }

    const options = {types: ['address']};
    this.autocomplete = new window.google.maps.places.Autocomplete(input, options);

    this._boundFillInAddress = this._fillInAddress.bind(this);
    window.google.maps.event.addListener(this.autocomplete, 'place_changed', this._boundFillInAddress);

    input.addEventListener('blur', function () {
      setTimeout(this._boundFillInAddress, 0);
    }.bind(this));

    input.__autocomplete = this.autocomplete;
  },

  _tryGetHtmlInput(input) {
    while (!(input instanceof HTMLInputElement)) {
      if (!input.inputElement) {
        break;
      }

      input = input.inputElement;
    }

    return input;
  },

  _slotChanged(e) {
    this._saveSlottedNodeToIdHash(e.target);

    // Re-initialize to ensure our listeners are correctly set up.
    this._initialize();
  },

  _saveSlottedNodeToIdHash(slot) {
    let node = slot.assignedNodes()[0];

    if (!node) {
      return;
    }

    let inputId = slot.dataset.inputId;

    this.$[inputId] = node;
  },

  _fillInAddress: function () {
    let place = this.place = this.autocomplete.getPlace();
    if (!place) {
      return;
    }

    let subpremise = this.getAddressComponent(place, 'subpremise', 'long_name');
    let streetAddress = '';

    if (subpremise) {
      streetAddress += subpremise + "/";
    }

    this.$.address1Input.value = (streetAddress + this.getAddressComponent(place, 'street_number', 'short_name')
      + ' ' + this.getAddressComponent(place, 'route', 'long_name')).trim() || place.name;
    this.$.address2Input.value = '';
    this.$.address3Input.value = '';
    this.$.cityInput.value = this.getAddressComponent(place, 'locality', 'long_name');
    this.$.stateInput.value = this.getAddressComponent(place, 'administrative_area_level_1', 'short_name');
    this.$.zipCodeInput.value = this.getAddressComponent(place, 'postal_code', 'short_name');
    this.$.countryInput.value = this.getAddressComponent(place, 'country', 'long_name');

  },

  getAddressComponent: function (place, component, valueType) {
    if (place.address_components) {
      for (var i = 0; i < place.address_components.length; i++) {
        var componentType = place.address_components[i].types[0];
        if (componentType === component) {
          return place.address_components[i][valueType];
        }
      }
    }
    return '';
  },

  /**
   * Validate inputs that arent shown to be optional parsed in optionalFields
   * @return {boolean}
   */
  validate: function () {
    // default value
    let isValid = true;
    let inputs = Object.values(this.$);
    // convery the nodeList to array and slice the first to remove the lookup element as it shouldnt be required
    inputs = [].slice.call(inputs, 1);

    // loop through each input, if optional make sure it isnt required and go to the next input
    inputs.forEach(input => {
      if (this.isOptionalInput(input)) {
        input.removeAttribute('required');
        return;
      }

      // is not optional
      input.required = true;
      input.errorMessage = 'Required';
      if(typeof input.validate === "function") {
        isValid = input.validate() && isValid;
      }
    });

    return isValid;
  },

  /**
   * check if the input we pass in is a part of the optionalFields array
   * @param input
   * @return {boolean}
   */
  isOptionalInput: function (input) {
    for (let field of this.optionalFields) {
      // get the element from the array key we pass in
      let inputToCheck = this.$[`${field}Input`];
      if (input === inputToCheck) {
        // is in the optional fields array
        return true;
      }
    }

    // is not optional
    return false;
  }
});
